import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TESTS,
  GET_TEST_TYPES,
  REMOVE_TESTS,
  TEST_CORE_FRAGMENT,
} from '../../../apollo/tests.ts';
import { useFragment } from '../../../apollo/__generated__';
import _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  useDeleteItems,
  useFetchMore,
  useGetMe,
  useSelectItem,
} from '../../../utils/hooks.ts';
import { useTestsSearch } from '../../../components/tests/tests-hooks.tsx';
import Search from '../../../components/search.tsx';
import ActionsDropdown from '../../../components/table/actions-dropdown.tsx';
import Table from '../../../components/table/table.tsx';
import Tag from '../../../components/common/tag.tsx';
import {
  EntityCreatedRole,
  TestStatus,
} from '../../../apollo/__generated__/graphql.ts';
import {
  initialTestsSort,
  testsColumns,
  testsSortKeys,
} from '../../../components/tests/tests-constants.ts';
import { useSort } from '../../../components/table/table-utils.ts';
import useToggleContentCreator from '../../../components/common/use-toggle-content-creator.tsx';
import { NavBarEnum } from '../../../utils/enums.ts';
import LocalDate from '../../../components/common/local-date.tsx';

interface IProps {
  isModalMode?: boolean;
  setSelectedTests?: Dispatch<SetStateAction<number[]>>;
}

const TestsBrowser = ({ isModalMode = false, setSelectedTests }: IProps) => {
  const FETCH_LIMIT = 20;

  const { ContentToggle, isContentToggleAvailable, isShowTeacherContent } =
    useToggleContentCreator();
  const { isTeacher, isAdmin, isTA } = useGetMe();

  const [page, setPage] = useState<number>(1);
  const [sort, onSetSort] = useSort(initialTestsSort);
  const { filter, onChangeFilter } = useTestsSearch();
  const { setIsDeleted, onDelete } = useDeleteItems();
  const { data: testTypesData } = useQuery(GET_TEST_TYPES);
  const { data, fetchMore, loading } = useQuery(GET_TESTS, {
    fetchPolicy: 'network-only',
    variables: {
      paging: {
        page: 1,
        limit: FETCH_LIMIT,
      },
      sort,
      filter: {
        ...filter,
        created: isContentToggleAvailable
          ? isShowTeacherContent
            ? undefined
            : EntityCreatedRole.Nalaprep
          : undefined,
      },
    },
  });
  const tests = useFragment(TEST_CORE_FRAGMENT, data?.test.getTests.results);
  const { selected, onSelect, onSelectAmount, onResetAll } = useSelectItem(
    tests?.map((test) => test.id) || [],
    setSelectedTests,
  );

  useEffect(() => {
    setPage(1);
  }, [filter, sort, isShowTeacherContent]);

  const [remove] = useMutation(REMOVE_TESTS, {
    refetchQueries: [GET_TESTS],
  });
  const onRemoveTests = (ids: number[]) => {
    remove({
      awaitRefetchQueries: true,
      refetchQueries: [GET_TESTS],
      variables: {
        input: {
          ids,
        },
      },
    });

    onResetAll();
    setPage(1);
    setIsDeleted(true);
  };

  const isHasPermissionForCreateTest = isTeacher || isAdmin || isTA;

  const onFetchMore = (setFetchMore: Dispatch<SetStateAction<boolean>>) => {
    const total = Number(data?.test.getTests.total);
    if (total && Number(tests?.length) < total) {
      const nextPage = page + 1;
      setPage(nextPage);
      setFetchMore(true);
      return fetchMore({
        variables: {
          paging: {
            page: nextPage,
            limit: FETCH_LIMIT,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.test?.getTests?.results) return prev;
          const union = [
            ...(prev.test?.getTests?.results || []),
            ...fetchMoreResult.test.getTests.results,
          ];
          const uniqueTests = _.uniqBy(union, 'id');

          if (uniqueTests.length !== union.length)
            console.error('duplicates on merge detected'); // TODO remove after dev stage

          return {
            test: {
              ...prev.test,
              getTests: {
                ...fetchMoreResult.test.getTests,
                results: uniqueTests,
              },
            },
          };
        },
      });
    }
  };

  const { ref, isFetchMore } = useFetchMore(onFetchMore, onDelete);
  const isLoading = isFetchMore || loading;

  return (
    <div>
      {!isModalMode && (
        <section className={'flex items-center justify-between'}>
          <div>
            <h1>{isTeacher ? 'My Tests' : 'Tests Browser'}</h1>
            <p className={'description'}>
              A list of all the tests in the database
            </p>
          </div>
          <div className={'flex gap-2'}>
            <ActionsDropdown
              selected={selected}
              browser={NavBarEnum.TESTS_BROWSER}
              onRemove={onRemoveTests}
            />
            {isHasPermissionForCreateTest && (
              <Link className={'link-blue-button'} to={'/create-test'}>
                Create test
              </Link>
            )}
            {(isAdmin || isTeacher) && (
              <Link to={'/scripts-browser'} className={'link-white-button'}>
                Scripts
              </Link>
            )}
          </div>
        </section>
      )}

      <div className={'mt-2 flex items-center justify-between'}>
        <div className={'flex'}>
          <Search
            onChangeFilter={onChangeFilter}
            searchBy={[
              { key: 'name', title: 'Name' },
              { key: 'description', title: 'Description' },
              { key: 'notes', title: 'Notes' },
              { key: 'typeId', title: 'Type' },
              { key: 'status', title: 'Status' },
            ]}
            optionsData={{
              typeId:
                testTypesData?.test.testTypes.map((type) => ({
                  key: String(type.id),
                  title: type.name,
                })) || [],
              status: Object.values(TestStatus).map((status) => ({
                key: status,
                title: status,
              })),
            }}
          />
          {isContentToggleAvailable ? <ContentToggle /> : null}
        </div>
        <div className={'flex flex-col whitespace-nowrap text-sm text-gray'}>
          <p>{data?.test.getTests.total} tests match your filters</p>
          <p>{selected.length} tests selected</p>
        </div>
      </div>

      <hr className={'my-2 opacity-10'} />

      <Table
        columns={testsColumns}
        selected={selected}
        onSelectAmount={onSelectAmount}
        onResetAll={onResetAll}
        isLoading={isLoading}
        sort={sort}
        setSort={onSetSort}
        sortKeys={testsSortKeys}
        isEmpty={!tests?.length}
      >
        {(tests || []).map((test, index, array) => {
          return (
            <tr key={test.id} ref={index === array.length - 5 ? ref : null}>
              <td>
                <div className={'flex justify-center'}>
                  <input
                    type={'checkbox'}
                    checked={selected.includes(test.id)}
                    onChange={() => onSelect(test.id)}
                  />
                </div>
              </td>
              <td>
                <Link
                  to={`${NavBarEnum.TESTS_BROWSER}/${test.slug || test.id}`}
                  className={'font-medium text-blue hover:underline'}
                >
                  {test.name}
                </Link>
              </td>
              <td>{test.description || ''}</td>
              <td>{test.notes || ''}</td>
              <td className={'w-36 whitespace-nowrap'}>{test.type.name}</td>
              <td className={'w-24 whitespace-nowrap'}>{test.questionCount}</td>
              <td className={'w-40 whitespace-nowrap'}>
                <LocalDate date={test.createdAt} format={'MMM d, yyyy h:mm a'} />
              </td>
              <td className={'w-28 whitespace-nowrap'}>
                <Tag
                  title={test.status}
                  color={
                    test.status === TestStatus.Active ? 'green' : undefined
                  }
                />
              </td>
              <td className={'w-24 whitespace-nowrap'}>{test.id}</td>
              <td className={'w-32 whitespace-nowrap'}>{test.created}</td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
};
export default TestsBrowser;
